import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {
  Briefcase,
  Building2,
  Calendar,
  ChevronDown,
  FileText,
  Globe,
  Hash,
  MapPin,
  User,
  Users,
} from 'lucide-react';

import type {
  ExtractRegistryInformationAdapterRawByVendor,
  RegistryInformationAdapterVendor,
} from './utils';
import { ctw } from '@/common';
import { CopyButton } from '@/components';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/atoms/Collapsible';

dayjs.extend(advancedFormat);

export type Officer = {
  name: string;
  position?: string | null;
  occupation?: string | null;
  address?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  isInactive?: boolean | null;
  currentStatus?: string | null;
};

export type Identifier = {
  label: string;
  value: string;
};

export type Industry = {
  label: string;
  value: string;
};

export type NameRecord = {
  label: string;
  value: string;
};

export type BaseRegistryDataDisplayProps = {
  className?: string;
  error?: string | null;

  processed: {
    // Source metadata
    source?: string | null;
    sourceUrl?: string | null;
    fetchedAt?: string | null;
    lastUpdatedAt?: string | null;

    // Company information
    companyName?: string | null;
    companyNumber?: string | null;
    jurisdictionCode?: string | null;
    companyType?: string | null;
    currentStatus?: string | null;
    isBranch?: boolean | null;
    branchStatus?: string | null;

    // Dates
    incorporationDate?: string | null;
    dissolutionDate?: string | null;

    // Address
    registeredAddress?: string | null;

    // Agent
    agentName?: string | null;

    // Employees
    numberOfEmployees?: number | null;

    // Arrays
    identifiers?: Identifier[];
    industries?: Industry[];
    previousNames?: NameRecord[];
    alternativeNames?: NameRecord[];

    // Officers - pre-separated by active/inactive status
    activeOfficers?: Officer[];
    inactiveOfficers?: Officer[];

    // Display options
    initialActiveOfficersLimit?: number;
  };
};

export type RegistryDataDisplayProps<TVendor extends RegistryInformationAdapterVendor> =
  | BaseRegistryDataDisplayProps
  | (BaseRegistryDataDisplayProps & {
      raw: ExtractRegistryInformationAdapterRawByVendor<TVendor>;
      renderRaw: (props: {
        raw: ExtractRegistryInformationAdapterRawByVendor<TVendor>;
      }) => React.ReactNode;
    });

const DataRow: React.FC<{
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  value: React.ReactNode;
  copyValue?: string;
  disableTranslation?: boolean;
}> = ({ label, Icon, value, copyValue, disableTranslation = false }) => {
  if (!value) return null;

  return (
    <div className="mb-4 grid grid-cols-2 items-center gap-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 flex-shrink-0 text-zinc-500" />
        <p className="text-sm text-slate-400">{label}</p>
      </div>
      <div className="flex items-center gap-1.5">
        <div
          className={ctw('text-sm text-gray-900', { notranslate: disableTranslation })}
          {...(disableTranslation && { translate: 'no' })}
        >
          {value}
        </div>
        {copyValue && <CopyButton textToCopy={copyValue} />}
      </div>
    </div>
  );
};

const ArrayDataRow: React.FC<{
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  items: Array<{ label: string; value: string }>;
  disableTranslation?: boolean;
}> = ({ label, Icon, items, disableTranslation = false }) => {
  if (!items || items.length === 0) return null;

  return (
    <div className="mb-4 grid grid-cols-2 items-center gap-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 flex-shrink-0 text-zinc-500" />
        <p className="text-sm text-slate-400">{label}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {items.map((item, index) => (
          <div
            key={index}
            className={ctw('flex items-center gap-1.5 text-sm text-gray-900', {
              notranslate: disableTranslation,
            })}
            {...(disableTranslation && { translate: 'no' })}
          >
            <span>{item.label}</span>
            <CopyButton textToCopy={item.value} />
            {index < items.length - 1 && <span className="text-slate-400">,</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const getStatusText = (officer: Officer): string => {
  if (officer.isInactive) {
    return 'Inactive';
  }

  if (officer.currentStatus) {
    return officer.currentStatus;
  }

  return 'Active';
};

const getStatusColor = (officer: Officer): string => {
  if (officer.isInactive) {
    return 'text-red-600';
  }

  return 'text-green-600';
};

const OfficerCard: React.FC<{ officer: Officer }> = ({ officer }) => {
  const statusText = getStatusText(officer);
  const statusColor = getStatusColor(officer);

  const dateRange =
    officer.startDate || officer.endDate
      ? `${officer.startDate ?? 'Unknown'} - ${officer.endDate ?? 'Present'}`
      : null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 flex-shrink-0 text-zinc-500" />
          <span className="notranslate text-sm font-medium text-gray-900" translate="no">
            {officer.name}
          </span>
          <CopyButton textToCopy={officer.name} />
        </div>
        <span className={ctw('text-xs font-medium', statusColor)}>{statusText}</span>
      </div>

      {officer.position && (
        <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <Briefcase className="h-3 w-3 text-zinc-400" />
          <span>{officer.position}</span>
        </div>
      )}

      {officer.occupation && (
        <div className="mt-1 text-xs text-slate-500">Occupation: {officer.occupation}</div>
      )}

      {dateRange && (
        <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
          <Calendar className="h-3 w-3 text-zinc-400" />
          <span>{dateRange}</span>
        </div>
      )}

      {officer.address && (
        <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
          <MapPin className="h-3 w-3 text-zinc-400" />
          <span>{officer.address}</span>
        </div>
      )}
    </div>
  );
};

const DEFAULT_INITIAL_ACTIVE_OFFICERS_LIMIT = 5;

type OfficersSectionProps = {
  activeOfficers: Officer[];
  inactiveOfficers: Officer[];
  initialActiveOfficersLimit?: number;
};

const OfficersSection: React.FC<OfficersSectionProps> = ({
  activeOfficers,
  inactiveOfficers,
  initialActiveOfficersLimit = DEFAULT_INITIAL_ACTIVE_OFFICERS_LIMIT,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const totalCount = activeOfficers.length + inactiveOfficers.length;

  if (totalCount === 0) return null;

  const initialActiveOfficers = activeOfficers.slice(0, initialActiveOfficersLimit);
  const remainingActiveOfficers = activeOfficers.slice(initialActiveOfficersLimit);
  const hasHiddenOfficers = remainingActiveOfficers.length > 0 || inactiveOfficers.length > 0;

  const getExpandButtonText = (): string => {
    const parts: string[] = [];

    if (remainingActiveOfficers.length > 0) {
      parts.push(`${remainingActiveOfficers.length} active`);
    }

    if (inactiveOfficers.length > 0) {
      parts.push(`${inactiveOfficers.length} inactive`);
    }

    const suffix = parts.length > 0 ? ` (+${parts.join(', ')})` : '';

    return `Show all ${totalCount} officers${suffix}`;
  };

  return (
    <div className="mt-6">
      <div className="mb-3 flex items-center gap-2">
        <Users className="h-4 w-4 text-zinc-500" />
        <h3 className="text-sm font-medium text-slate-700">Officers & Directors ({totalCount})</h3>
      </div>

      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {activeOfficers.length > 0 && (
          <div className="mb-4">
            <p className="mb-2 text-xs text-slate-500">Active ({activeOfficers.length})</p>
            <div className="grid gap-2">
              {initialActiveOfficers.map((officer, index) => (
                <OfficerCard key={`active-${index}`} officer={officer} />
              ))}
            </div>

            {remainingActiveOfficers.length > 0 && (
              <CollapsibleContent
                className={ctw(
                  'mt-2 grid gap-2',
                  'data-[state=open]:animate-in data-[state=closed]:animate-out',
                  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                  'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
                )}
              >
                {remainingActiveOfficers.map((officer, index) => (
                  <OfficerCard key={`active-remaining-${index}`} officer={officer} />
                ))}
              </CollapsibleContent>
            )}
          </div>
        )}

        {inactiveOfficers.length > 0 && (
          <CollapsibleContent
            className={ctw(
              'mb-4',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
            )}
          >
            <p className="mb-2 text-xs text-slate-500">Inactive ({inactiveOfficers.length})</p>
            <div className="grid gap-2">
              {inactiveOfficers.map((officer, index) => (
                <OfficerCard key={`inactive-${index}`} officer={officer} />
              ))}
            </div>
          </CollapsibleContent>
        )}

        {hasHiddenOfficers && (
          <CollapsibleTrigger className="flex items-center gap-1 text-sm text-blue-600 transition-colors hover:text-blue-800 hover:underline">
            {isOpen ? 'Show less' : getExpandButtonText()}
            <ChevronDown className={ctw('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
          </CollapsibleTrigger>
        )}
      </Collapsible>
    </div>
  );
};

const formatDate = (dateString: string) => {
  return dayjs(dateString).format('MMM Do YYYY');
};

const checkIsRegistryDataDisplayPropsWithRaw = <TVendor extends RegistryInformationAdapterVendor>(
  props: ComponentProps<typeof RegistryDataDisplay<TVendor>>,
): props is Extract<ComponentProps<typeof RegistryDataDisplay<TVendor>>, { raw: any }> => {
  return 'raw' in props && !!props.raw;
};

export const RegistryDataDisplay = <TVendor extends RegistryInformationAdapterVendor>(
  props: RegistryDataDisplayProps<TVendor>,
) => {
  const {
    source,
    sourceUrl,
    fetchedAt,
    lastUpdatedAt,
    companyName,
    companyNumber,
    jurisdictionCode,
    companyType,
    currentStatus,
    isBranch,
    branchStatus,
    incorporationDate,
    dissolutionDate,
    registeredAddress,
    agentName,
    numberOfEmployees,
    identifiers,
    industries,
    previousNames,
    alternativeNames,
    activeOfficers,
    inactiveOfficers,
    initialActiveOfficersLimit,
  } = props.processed;
  const propsWithRaw = checkIsRegistryDataDisplayPropsWithRaw(props) ? props : undefined;
  const hasOfficers = (activeOfficers?.length ?? 0) > 0 || (inactiveOfficers?.length ?? 0) > 0;

  const hasNonEmptyValues = (obj: Record<string, unknown>): boolean => {
    return Object.values(obj).some(value => {
      if (value === null || value === undefined) {
        return false;
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return true;
    });
  };

  const hasProcessedData = hasNonEmptyValues(props.processed);
  const hasRawData = hasNonEmptyValues(propsWithRaw?.raw ?? {});
  const hasData = hasProcessedData || hasRawData;

  if (props.error || !hasData) {
    const errorMessage = props.error || 'Registry data unavailable';

    return (
      <div className={ctw('rounded-md border border-slate-200 bg-slate-50 p-4', props.className)}>
        <p className="text-sm text-slate-600">{errorMessage}</p>
      </div>
    );
  }

  return (
    <div className={props.className}>
      {(source || fetchedAt || lastUpdatedAt) && (
        <div className="mb-6 flex items-center gap-2 text-sm text-slate-500">
          {source && (
            <>
              <span>
                Source:{' '}
                {sourceUrl ? (
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="notranslate text-blue-600 hover:underline"
                    translate="no"
                  >
                    {source}
                  </a>
                ) : (
                  <span className="notranslate" translate="no">
                    {source}
                  </span>
                )}
              </span>
              {(fetchedAt || lastUpdatedAt) && <span>|</span>}
            </>
          )}
          {fetchedAt && (
            <>
              <span>Fetched: {formatDate(fetchedAt)}</span>
              {lastUpdatedAt && <span>|</span>}
            </>
          )}
          {lastUpdatedAt && <span>Last updated: {formatDate(lastUpdatedAt)}</span>}
        </div>
      )}

      {/* Basic Company Information */}
      <DataRow
        label="Company Name"
        Icon={Building2}
        value={companyName}
        copyValue={companyName || undefined}
        disableTranslation
      />

      <DataRow
        label="Company Number"
        Icon={Hash}
        value={companyNumber}
        copyValue={companyNumber || undefined}
        disableTranslation
      />

      {/* Identifiers */}
      {identifiers && identifiers.length > 0 && (
        <ArrayDataRow
          label="External Identifiers"
          Icon={Hash}
          items={identifiers}
          disableTranslation
        />
      )}

      <DataRow
        label="Jurisdiction"
        Icon={Globe}
        value={jurisdictionCode}
        copyValue={jurisdictionCode || undefined}
      />

      <DataRow
        label="Company Type"
        Icon={FileText}
        value={companyType}
        copyValue={companyType || undefined}
      />

      <DataRow
        label="Current Status"
        Icon={FileText}
        value={currentStatus}
        copyValue={currentStatus || undefined}
      />

      {/* Branch Information */}
      {isBranch && (
        <DataRow
          label="Branch Status"
          Icon={FileText}
          value={branchStatus}
          copyValue={branchStatus || undefined}
        />
      )}

      {/* Dates */}
      <DataRow
        label="Incorporation Date"
        Icon={Calendar}
        value={incorporationDate}
        copyValue={incorporationDate || undefined}
      />

      <DataRow
        label="Dissolution Date"
        Icon={Calendar}
        value={dissolutionDate}
        copyValue={dissolutionDate || undefined}
      />

      {/* Address */}
      <DataRow
        label="Registered Address"
        Icon={MapPin}
        value={registeredAddress}
        copyValue={registeredAddress || undefined}
      />

      {/* Agent */}
      <DataRow
        label="Agent Name"
        Icon={Users}
        value={agentName}
        copyValue={agentName || undefined}
        disableTranslation
      />

      {/* Number of Employees */}
      <DataRow
        label="Number of Employees"
        Icon={Users}
        value={numberOfEmployees?.toString()}
        copyValue={numberOfEmployees?.toString()}
      />

      {/* Industries */}
      {industries && industries.length > 0 && (
        <ArrayDataRow
          label="Industry Codes"
          Icon={Building2}
          items={industries}
          disableTranslation={false}
        />
      )}

      {/* Previous Names */}
      {previousNames && previousNames.length > 0 && (
        <ArrayDataRow
          label="Previous Names"
          Icon={FileText}
          items={previousNames}
          disableTranslation
        />
      )}

      {/* Alternative Names */}
      {alternativeNames && alternativeNames.length > 0 && (
        <ArrayDataRow
          label="Alternative Names"
          Icon={FileText}
          items={alternativeNames}
          disableTranslation
        />
      )}

      {/* Officers & Directors */}
      {hasOfficers && (
        <OfficersSection
          activeOfficers={activeOfficers ?? []}
          inactiveOfficers={inactiveOfficers ?? []}
          initialActiveOfficersLimit={initialActiveOfficersLimit}
        />
      )}

      {propsWithRaw?.raw && propsWithRaw.renderRaw?.({ raw: propsWithRaw.raw })}
    </div>
  );
};
